import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const PAGE_LIMIT = 10
const CATEGORIES = ['recent', 'upcoming', 'past', 'canceled', 'archived']

function emptyCategoryState() {
  return {
    items: [],
    page: { limit: PAGE_LIMIT, number: 1, total_pages: 0, total_records: 0, current_page: 0 },
    fetchedAt: null,
    loading: false,
    error: null,
  }
}

function emptyCountsState() {
  return { recent: null, upcoming: null, past: null, canceled: null, archived: null, loading: false, error: null }
}

function emptyEventState() {
  return {
    categories: Object.fromEntries(CATEGORIES.map((category) => [category, emptyCategoryState()])),
    counts: emptyCountsState(),
  }
}

export const useEventBookingsStore = defineStore('eventBookings', {
  state: () => ({
    byEventId: {}, // { [eventId]: { categories: { ... }, counts: { ... } } }
  }),

  getters: {
    categoryState: (state) => (eventId, category) =>
      state.byEventId[eventId]?.categories?.[category] || emptyCategoryState(),

    countsState: (state) => (eventId) =>
      state.byEventId[eventId]?.counts || emptyCountsState(),
  },

  actions: {
    ensureEvent(eventId) {
      if (!this.byEventId[eventId]) this.byEventId[eventId] = emptyEventState()
      return this.byEventId[eventId]
    },

    // Always fetches live — no TTL cache for bookings.
    async fetchIfStale(eventId, category, page = 1, dateFrom = null, dateTo = null) {
      await this.fetchCategory(eventId, category, page, dateFrom, dateTo)
    },

    async fetchCategory(eventId, category, page = 1, dateFrom = null, dateTo = null) {
      const cat = this.ensureEvent(eventId).categories[category]
      cat.loading = true
      cat.error = null
      try {
        const { axiosRequest } = useRequest()
        let url = `/api/v1/events/${eventId}/bookings?category=${category}&page[number]=${page}&page[limit]=${PAGE_LIMIT}`
        if (dateFrom) url += `&date_from=${dateFrom}`
        if (dateTo)   url += `&date_to=${dateTo}`
        const res = await axiosRequest('GET', url, {}, false)
        if (!res.js_error && res.response.success) {
          cat.items = res.response.data.records
          cat.page = res.response.data.page
          cat.fetchedAt = Date.now()
        } else {
          cat.error = 'Failed to load bookings.'
        }
      } catch {
        cat.error = 'Something went wrong.'
      } finally {
        cat.loading = false
      }
    },

    async fetchCounts(eventId, dateFrom = null, dateTo = null) {
      const counts = this.ensureEvent(eventId).counts
      counts.loading = true
      counts.error = null
      try {
        const { axiosRequest } = useRequest()
        let url = `/api/v1/events/${eventId}/bookings_counts`
        const qs = []
        if (dateFrom) qs.push(`date_from=${dateFrom}`)
        if (dateTo)   qs.push(`date_to=${dateTo}`)
        if (qs.length) url += `?${qs.join('&')}`
        const res = await axiosRequest('GET', url, {}, false)
        if (!res.js_error && res.response.success) {
          Object.assign(counts, res.response.data)
        } else {
          counts.error = 'Failed to load counts.'
        }
      } catch {
        counts.error = null
      } finally {
        counts.loading = false
      }
    },

    async archiveBooking(eventId, id) {
      return this.toggleArchive(eventId, id, 'archive')
    },

    async unarchiveBooking(eventId, id) {
      return this.toggleArchive(eventId, id, 'unarchive')
    },

    async cancelBooking(eventId, booking, reason) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('DELETE', `/api/v1/public/bookings/${booking.private_key}/cancel`,
        { booking: { canceled_reason: reason } }, false)
      if (res.js_error || !res.response?.success) {
        return { success: false, message: res.response?.message || 'Something went wrong.' }
      }

      const event = this.ensureEvent(eventId)
      ;['recent', 'upcoming'].forEach((category) => this.removeFromCategory(event, category, booking.id))
      const canceled = event.categories.canceled
      if (canceled.fetchedAt) canceled.items.unshift({ ...booking, status: 'canceled', canceled_reason: reason })

      return { success: true, message: res.response.message }
    },

    async toggleArchive(eventId, id, action) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/bookings/${id}/${action}`, {}, false)
      if (res.js_error || !res.response?.success) {
        return { success: false, message: res.response?.message || 'Something went wrong.' }
      }

      const updated = res.response.data
      const event = this.ensureEvent(eventId)

      if (action === 'archive') {
        CATEGORIES.filter((category) => category !== 'archived').forEach((category) => this.removeFromCategory(event, category, id))
        const archived = event.categories.archived
        if (archived.fetchedAt) archived.items.unshift(updated)
      } else {
        this.removeFromCategory(event, 'archived', id)
        CATEGORIES.filter((category) => category !== 'archived').forEach((category) => this.invalidateCategory(eventId, category))
      }

      return { success: true, booking: updated, message: res.response.message }
    },

    removeFromCategory(event, category, id) {
      const cat = event.categories[category]
      cat.items = cat.items.filter((booking) => booking.id !== id)
    },

    async updateInternalNotes(eventId, id, internalNotes) {
      const { axiosRequest } = useRequest()
      const res = await axiosRequest('PUT', `/api/v1/bookings/${id}`, { booking: { internal_notes: internalNotes } }, false)
      if (res.js_error || !res.response?.success) {
        return { success: false, message: res.response?.message || 'Something went wrong.' }
      }

      const updated = res.response.data
      const event = this.ensureEvent(eventId)
      CATEGORIES.forEach((category) => {
        const booking = event.categories[category].items.find((b) => b.id === id)
        if (booking) booking.internal_notes = updated.internal_notes
      })

      return { success: true, booking: updated, message: res.response.message }
    },

    invalidateCategory(eventId, category) {
      const cat = this.byEventId[eventId]?.categories?.[category]
      if (cat) cat.fetchedAt = null
    },

    invalidate(eventId) {
      if (eventId) {
        delete this.byEventId[eventId]
      } else {
        this.byEventId = {}
      }
    },
  },
})
